// ===========================================
// useViewTerms: the term list for the page the user is on (#272)
// ===========================================
// GET /admin/terms needs school_setup.read, so a plain teacher, parent or student got a 403 and
// an empty list: no Notes term filter, no current-term default on the teacher Classes page, no
// week numbers on the teacher calendar. A caller who may read the admin list (the owner, a staff
// role with school_setup.read) still gets it, with each term's lock; everyone else reads
// GET /calendar/terms, which every role may read. Exactly one of the two queries is enabled.
//
// Caches: term edits invalidate the admin key only. Anyone who can edit terms holds
// school_setup and reads that key; the calendar key is read only by those who cannot edit terms.

import type { UseQueryResult } from '@tanstack/react-query';
import { useTerms } from './useAdmin';
import { useCalendarTerms } from './useHybridBooking';
import { usePermissions } from './usePermissions';
import type { TermOption } from '../api/hybridBooking.api';

export function useViewTerms(): UseQueryResult<TermOption[]> {
  const { can } = usePermissions();
  const canReadAdminTerms = can('school_setup', 'read');
  const admin = useTerms({ enabled: canReadAdminTerms });
  const view = useCalendarTerms({ enabled: !canReadAdminTerms });
  return canReadAdminTerms ? admin : view;
}
